import React, { createContext, useContext, useState, useEffect } from 'react';

export const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('afsc-theme');
    if (savedTheme) {
      return savedTheme;
    }
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    // Default to dark
    return 'dark';
  });

  const [transitionState, setTransitionState] = useState({
    isTransitioning: false,
    clickPosition: null,
    nextTheme: null,
  });

  useEffect(() => {
    // Save theme to localStorage
    localStorage.setItem('afsc-theme', theme);
    // Update document class for CSS variables
    document.documentElement.setAttribute('data-theme', theme);
    
    // Add/remove transition class
    if (transitionState.isTransitioning) {
      document.body.classList.add('theme-transitioning');
    } else {
      document.body.classList.remove('theme-transitioning');
    }
  }, [theme, transitionState.isTransitioning]);

  const toggleTheme = (clickPosition = null) => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    
    if (clickPosition) {
      // Start transition with circular reveal
      setTransitionState({
        isTransitioning: true,
        clickPosition,
        nextTheme,
      });
    } else {
      // Direct theme change (no animation)
      setTheme(nextTheme);
    }
  };

  const completeTransition = () => {
    if (transitionState.nextTheme) {
      setTheme(transitionState.nextTheme);
      setTransitionState({
        isTransitioning: false,
        clickPosition: null,
        nextTheme: null,
      });
    }
  };

  const value = {
    theme,
    toggleTheme,
    transitionState,
    completeTransition,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

